import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  FormControl,
  FormLabel,
  Flex,
  SimpleGrid,
  Select,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  UnorderedList,
  ListItem,
  Badge,
} from '@chakra-ui/react';
import { getData, postData } from '../../utils/httpClient';

/**
 * Modal de creación de INSCRIPCIÓN A EXAMEN.
 *
 * Flujo:
 *   1) Se elige el alumno.
 *   2) Al elegir alumno se consulta GET /eligibility/exam-subjects
 *      para traer SOLO las materias habilitadas por reglamento
 *      (art 32/48/49 + evaluación docente).
 *   3) Elegida la materia, se cargan los exámenes programados para esa
 *      materia (GET /exam/schedule?subjectId=...).
 *   4) Confirmado, se hace POST /exam/inscriptions; los errores de
 *      reglamento que no cierren (art 35, deuda, etc) se muestran
 *      literalmente en un toast rojo.
 *
 * El padre sólo recibe `onSuccess` para refrescar la grilla. Se
 * conserva `onConfirm` como alias por compatibilidad.
 */
export default function CreateInscriptionModal({
  isOpen,
  onClose,
  onConfirm,
  onSuccess,
}) {
  const toast = useToast();

  const [students, setStudents] = useState([]);
  const [eligibleSubjects, setEligibleSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  // Diagnóstico detallado por alumno: se llama siempre después de
  // elegir alumno para reportar por qué una materia NO es elegible,
  // en vez de mostrar sólo "sin materias elegibles".
  const [diagnose, setDiagnose] = useState(null);

  const [studentId, setStudentId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [examScheduleId, setExamScheduleId] = useState('');

  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingExams, setLoadingExams] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStudentId('');
    setSubjectId('');
    setExamScheduleId('');
    setEligibleSubjects([]);
    setExams([]);
    setDiagnose(null);
  };

  const loadStudents = async () => {
    try {
      const response = await getData('/person/student?page=1&limit=500&data=');
      setStudents(response?.payload?.people || []);
    } catch (error) {
      toast({
        title: 'Error al cargar los estudiantes.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadEligibleSubjects = async (sid) => {
    if (!sid) {
      setEligibleSubjects([]);
      setDiagnose(null);
      return;
    }
    setLoadingSubjects(true);
    try {
      // role=BEDELIA: desde el panel admin no se exige evaluación
      // docente; los demás requisitos académicos se siguen aplicando.
      // Pedimos en paralelo el listado elegible y el diagnóstico
      // detallado, así siempre podemos mostrar el motivo si la lista
      // queda vacía (o si una materia esperada no aparece).
      const [elig, diag] = await Promise.all([
        getData(`/eligibility/exam-subjects?studentId=${sid}&role=BEDELIA`),
        getData(`/eligibility/exam-diagnose?studentId=${sid}&role=BEDELIA`),
      ]);
      setEligibleSubjects(elig?.payload?.subjects || []);
      setDiagnose(diag?.payload || null);
    } catch (error) {
      setEligibleSubjects([]);
      setDiagnose(null);
      toast({
        title: 'No se pudieron cargar materias elegibles',
        description: error?.message,
        status: 'error',
      });
    } finally {
      setLoadingSubjects(false);
    }
  };

  const loadExamsForSubject = async (subId) => {
    if (!subId) {
      setExams([]);
      return;
    }
    setLoadingExams(true);
    try {
      const response = await getData(`/exam/schedule?subjectId=${subId}&page=1&limit=50`);
      setExams(response?.payload?.exams || []);
    } catch (error) {
      setExams([]);
      toast({
        title: 'No se pudieron cargar exámenes',
        description: error?.message,
        status: 'error',
      });
    } finally {
      setLoadingExams(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    reset();
    loadStudents();
  }, [isOpen]);

  useEffect(() => {
    setSubjectId('');
    setExamScheduleId('');
    setExams([]);
    loadEligibleSubjects(studentId);
  }, [studentId]);

  useEffect(() => {
    setExamScheduleId('');
    loadExamsForSubject(subjectId);
  }, [subjectId]);

  const submit = async () => {
    if (!studentId || !subjectId || !examScheduleId) {
      toast({
        title: 'Complete todos los campos',
        status: 'warning',
        duration: 2500,
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await postData('/exam/inscriptions', {
        studentId: Number(studentId),
        examScheduleId: Number(examScheduleId),
        // El panel admin siempre inscribe en nombre de BEDELIA: esto
        // permite saltar evaluación docente y cancelación tardía (que
        // sólo aplican al portal del alumno). Las reglas académicas
        // duras (curso aprobado, previas, 4 aplazos) se mantienen.
        role: 'BEDELIA',
      });
      toast({
        title: 'Inscripción registrada',
        description: `Código: ${res?.payload?.verificationCode || '-'}`,
        status: 'success',
      });
      if (onSuccess) onSuccess();
      if (onConfirm) onConfirm();
      onClose && onClose();
    } catch (error) {
      toast({
        title: 'No se pudo inscribir al examen',
        description: error?.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        maxWidth={{ base: '100%', md: '80%' }}
        minHeight={'85vh'}
        maxHeight={'85vh'}
        overflowY="auto"
      >
        <ModalHeader mb={5}>Inscripción a examen</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
          >
            <Box flex="1" mb={5}>
              <SimpleGrid columns={{ base: 1 }} spacing={5} mt={4}>
                <FormControl id="studentId" isRequired>
                  <FormLabel>Alumno</FormLabel>
                  <Select
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                  >
                    <option value="">Seleccione</option>
                    {students.map((p) => {
                      const fullName = [p.name, p.lastName]
                        .filter(Boolean)
                        .join(' ');
                      const doc = p.document ? ` (${p.document})` : '';
                      return (
                        <option key={p.id} value={p.id}>
                          {fullName}
                          {doc}
                        </option>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl id="subjectId" isRequired isDisabled={!studentId}>
                  <FormLabel>
                    Materia elegible{' '}
                    {loadingSubjects && (
                      <Text as="span" fontSize="xs" color="gray.500">
                        (cargando…)
                      </Text>
                    )}
                  </FormLabel>
                  <Select
                    value={subjectId}
                    onChange={(e) => setSubjectId(e.target.value)}
                  >
                    <option value="">
                      {eligibleSubjects.length
                        ? 'Seleccione'
                        : studentId
                        ? 'Sin materias elegibles'
                        : 'Primero elija un alumno'}
                    </option>
                    {eligibleSubjects.map((s) => (
                      <option key={s.subjectId} value={s.subjectId}>
                        {s.description}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Sólo aparecen materias donde el alumno tiene curso
                    aprobado vigente, sin deuda, sin 4 aplazos y con previas al
                    día. (Evaluación docente no se exige desde el panel admin.)
                  </Text>
                </FormControl>

                {/*
                  Diagnóstico por alumno: si existe, detalla qué materias
                  están OK y cuáles están bloqueadas con el motivo exacto.
                  Así Bedelía sabe por qué una materia no aparece en el
                  combo sin tener que mirar la base.
                */}
                {studentId && diagnose && (
                  <Box mt={2}>
                    {/*
                      Si el alumno no tiene ningún course_grade, el
                      backend reporta eso en globalReasons y subjects
                      queda vacío. En ese caso alcanza con el banner
                      amarillo y además sugerimos el flujo de alta.
                    */}
                    {diagnose.globalReasons && diagnose.globalReasons.length > 0 && (
                      <Alert status="warning" borderRadius="md" mb={2}>
                        <AlertIcon />
                        <AlertDescription>
                          <Text fontWeight="bold" mb={1}>
                            Bloqueos generales del alumno:
                          </Text>
                          <UnorderedList>
                            {diagnose.globalReasons.map((r, i) => (
                              <ListItem key={i}>{r}</ListItem>
                            ))}
                          </UnorderedList>
                          {(!diagnose.subjects || diagnose.subjects.length === 0) && (
                            <Text mt={2} fontSize="sm">
                              Para habilitarlo: 1) Inscribirlo al dictado
                              de la materia; 2) Cerrar el acta de ese
                              dictado con estado APROBADO o EXONERADO.
                            </Text>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                    {diagnose.subjects && diagnose.subjects.length > 0 && (
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={3}
                        fontSize="sm"
                      >
                        <Text fontWeight="bold" mb={2}>
                          Estado por materia del alumno
                        </Text>
                        {diagnose.subjects.map((s) => (
                          <Box key={s.subjectId} mb={2}>
                            <Flex align="center" gap={2}>
                              <Badge
                                colorScheme={s.eligible ? 'green' : 'red'}
                              >
                                {s.eligible ? 'HABILITADA' : 'BLOQUEADA'}
                              </Badge>
                              <Text fontWeight="bold">{s.description}</Text>
                              <Text color="gray.500">
                                (curso: {s.courseGradeStatus}
                                {s.courseValidUntil
                                  ? `, válido hasta ${s.courseValidUntil}`
                                  : ''}
                                )
                              </Text>
                            </Flex>
                            {!s.eligible && s.reasons.length > 0 && (
                              <UnorderedList ml={6} color="red.600">
                                {s.reasons.map((r, i) => (
                                  <ListItem key={i}>{r}</ListItem>
                                ))}
                              </UnorderedList>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                <FormControl id="examScheduleId" isRequired isDisabled={!subjectId}>
                  <FormLabel>
                    Examen{' '}
                    {loadingExams && (
                      <Text as="span" fontSize="xs" color="gray.500">
                        (cargando…)
                      </Text>
                    )}
                  </FormLabel>
                  <Select
                    value={examScheduleId}
                    onChange={(e) => setExamScheduleId(e.target.value)}
                  >
                    <option value="">
                      {exams.length
                        ? 'Seleccione'
                        : subjectId
                        ? 'No hay exámenes programados'
                        : 'Primero elija una materia'}
                    </option>
                    {exams.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.exam_date}
                        {e.exam_time ? ` ${e.exam_time}` : ''} — {e.location || 'S/L'}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Box>
            <Button
              colorScheme="blue"
              onClick={submit}
              width="120px"
              isDisabled={submitting || !studentId || !subjectId || !examScheduleId}
            >
              Completar
            </Button>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
